import React, { useEffect, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import { skills } from "../data/mock";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add("in"), io.unobserve(e.target))),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const SkillBar = ({ item, i }) => {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(item.level), 120 + i * 80);
    return () => clearTimeout(t);
  }, [item.level, i]);
  return (
    <div className="group py-4 border-b hairline" data-cursor-hover>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-display text-lg md:text-xl">{item.name}</span>
        <span className="font-mono text-[11px] text-muted-foreground">{item.level}%</span>
      </div>
      <div className="h-px bg-border relative overflow-hidden">
        <div
          className="absolute left-0 top-0 h-px bg-foreground transition-[width] duration-[1400ms] ease-[cubic-bezier(.2,.7,.2,1)]"
          style={{ width: `${w}%` }}
        />
      </div>
    </div>
  );
};

const Skills = () => {
  useReveal();
  const categories = Object.keys(skills);
  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-12 md:pb-16">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
            03 — Skills
          </div>
          <h1 className="display-xl text-5xl md:text-8xl leading-[0.95]">
            The <span className="italic font-light">toolkit.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-muted-foreground text-lg leading-relaxed">
            A focused stack I use every day — backend-first, cloud-native and
            observability-aware. Levels are honest self-assessments.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          {categories.map((cat, ci) => (
            <div key={cat} className="reveal">
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-mono text-xs text-muted-foreground">0{ci + 1}</span>
                <SectionHeaderInline title={cat} />
              </div>
              <div>
                {skills[cat].map((s, i) => (
                  <SkillBar key={s.name} item={s} i={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Principles grid */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <SectionHeader
            index="03.2"
            eyebrow="How I work"
            title="Principles over tools."
            kicker="Frameworks change. These do not."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border hairline rounded-md overflow-hidden">
            {[
              { k: "01", t: "Ship small", d: "Tiny PRs, reviewable diffs, reversible deploys." },
              { k: "02", t: "Measure first", d: "Don’t optimise what you haven’t profiled." },
              { k: "03", t: "Write the doc", d: "If it isn’t documented, it doesn’t exist." },
              { k: "04", t: "Design for failure", d: "Retries, idempotency, dead-letter queues." },
              { k: "05", t: "Own the oncall", d: "Quality rises when the author wears the pager." },
              { k: "06", t: "Stay curious", d: "The next AI cert, the next DB paper, the next talk." },
            ].map((p, i) => (
              <div
                key={p.k}
                className={`p-8 md:p-10 ${i % 3 !== 2 ? "md:border-r hairline" : ""} ${i < 3 ? "md:border-b hairline" : ""} border-b hairline hover:bg-muted/40 transition-colors`}
                data-cursor-hover
              >
                <div className="font-mono text-xs text-muted-foreground">{p.k}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-3">{p.t}</h3>
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const SectionHeaderInline = ({ title }) => (
  <h2 className="font-display text-2xl md:text-3xl tracking-tight">{title}</h2>
);

export default Skills;
